import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCart } from 'lucide-react';
import { MultilineText } from '@/presentation/component/ui';
import { formatCFA } from '@/presentation/util/format';
import type { Subcategory } from '@/domain/model/Subcategory';

interface PaymentSummaryProps {
  subcategory: Subcategory;
}

export const PaymentSummary = ({ subcategory }: PaymentSummaryProps) => {
  const { t } = useTranslation();

  const chapterCount = useMemo(() => subcategory.chapters?.length ?? 0, [subcategory.chapters]);

  return (
    <div className="bg-[var(--color-surface-2)] rounded-lg p-6 space-y-4">
      <h3 className="text-lg font-medium text-[var(--color-ink)] flex items-center gap-2">
        <ShoppingCart className="w-5 h-5" />
        <span>{t('payment.orderSummary')}</span>
      </h3>

      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-medium text-[var(--color-ink)]">{subcategory.name}</h4>
            <MultilineText className="text-sm text-[var(--color-ink-soft)] mt-1 line-clamp-2">
              {subcategory.description}
            </MultilineText>
            {chapterCount > 0 && (
              <p className="text-sm text-[var(--color-ink-faint)] mt-2">
                {t('payment.includesChapters', { count: chapterCount })}
              </p>
            )}
          </div>
          <div className="text-right ml-4">
            <span className="text-lg font-semibold text-[var(--color-ink)]">
              {formatCFA(subcategory.price)}
            </span>
          </div>
        </div>
      </div>

      <hr className="border-[var(--color-line)]" />

      <div className="flex justify-between text-base font-semibold">
        <span className="text-[var(--color-ink)]">{t('payment.total')}</span>
        <span className="text-[var(--color-ink)]">{formatCFA(subcategory.price)}</span>
      </div>

      <div className="bg-[var(--color-accent-tint)] border border-[var(--color-accent-soft)] rounded-lg p-4 mt-4">
        <h4 className="text-sm font-medium text-[var(--color-accent-ink)] mb-2">
          {t('payment.whatYouGet')}
        </h4>
        <ul className="text-sm text-[var(--color-accent-ink)] space-y-1">
          <li>• {t('payment.lifetimeAccess')}</li>
          <li>• {t('payment.allFutureLessons')}</li>
          <li>• {t('payment.mobileAccess')}</li>
          <li>• {t('payment.certificateCompletion')}</li>
        </ul>
      </div>

      <p className="text-center text-sm text-[var(--color-ink-faint)]">
        {t('payment.moneyBackGuarantee')}
      </p>
    </div>
  );
};
